import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { MDBCol, MDBRow } from 'mdbreact';
import { post } from 'axios';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import Overlay from 'components/Informations/Overlay';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Toast from 'components/Informations/Toast';

import InputField from 'components/FormGroup/InputField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import SelectField from 'components/FormGroup/SelectField';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';
import { nrleNumber } from 'utils/masks';

import * as S from './styles';

const { insertClient, getEmployees } = endpoints;

const validationSchema = Yup.object().shape({
  clientNrle: Yup.string()
    .min(18, 'Número de CNPJ inválido')
    .max(18, 'Número de CNPJ inválido')
    .required('Insira o número do CNPJ'),
  clientName: Yup.string().required('Insira o nome do cliente'),
  accountManager: Yup.number().required('Selecione o gerente de contas'),
  clientStatus: Yup.number().required('Selecione o status do cliente'),
});

function AddClients({
  location,
  history,
  handleSubmit,
  values,
  errors,
  setFieldValue,
  status,
  user,
}) {
  const { state } = location;

  const [formAlterations, setFormAlterations] = useState(false);

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');
  const [overlayAction, setOverlayAction] = useState(() => {});

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState([]);
  const [accountManagersOptions, setAccountManagersOptions] = useState([]);
  const [allEmployees] = useFetch(getEmployees);

  useEffect(() => {
    const toast = !!(status && Object.keys(status).length);

    if (toast) {
      setToastVisibility(true);
      setToastInfos(status);
    }
  }, [status]);

  useEffect(() => {
    (async () => {
      const { id_funcionario: userId } = user;
      setFieldValue('idFuncionario', userId);

      const response = await allEmployees;
      const filteredEmployees = response.filter(
        employee =>
          employee.id_funcao === 16 && employee.tipo_status === 'Ativo',
      );

      const accountsOptions = filteredEmployees.map(account => ({
        label: account.nome,
        value: account.id_funcionario,
      }));

      const optionsStatus = [
        { value: 1, label: 'Ativo' },
        { value: 2, label: 'Inativo' },
      ];

      if (state) {
        const {
          nome,
          cnpj,
          id_func_account_manager: idAccountManager,
          id_status_cliente: idStatus,
          id_cliente: idClient,
        } = state;

        const account = filteredEmployees.filter(
          employee => employee.id_funcionario === idAccountManager,
        );

        if (account.length) {
          const { id_funcionario: id } = account[0];
          setFieldValue('accountManager', id);
        }

        setFieldValue('clientId', idClient);
        setFieldValue('clientNrle', nrleNumber(cnpj));
        setFieldValue('clientStatus', idStatus);
        setFieldValue('clientName', nome);
      }

      setAccountManagersOptions(accountsOptions);
      setStatusOptions(optionsStatus);
      return accountsOptions.length ? setIsLoading(false) : setIsLoading(true);
    })();
  }, [allEmployees, setFieldValue, state, user]);

  const renderToast = (type, message, closeTime) => {
    setTimeout(() => {
      setToastVisibility(false);
      setToastInfos({});
    }, closeTime + 1500);

    return (
      <Toast type={type} message={message} autoClose={closeTime} hideProgress />
    );
  };

  const renderOverlay = type => {
    switch (type) {
      case 'cancel':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a atualização e isso fará com que todas as informações ',
              <span>não salvas</span>,
              ' sejam ',
              <span>descartadas</span>,
              ' Deseja continuar com esta ação?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
                history.push('/auth/clients');
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
              }}
            />
          </Overlay>
        );

      case 'returning':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as informações que não foram salvas. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
                history.push('/auth/clients');
              }}
            />
            <FilledButton
              text="Não, permanecer"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
              }}
            />
          </Overlay>
        );

      case 'addClient':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="success"
            text={[
              'Você está ',
              <span>
                {state
                  ? 'atualizando um cliente'
                  : 'adicionando um novo cliente'}
              </span>,
              '. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim"
              handleClick={() => {
                overlayAction();
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="confirm"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(prev => !prev);
              }}
            />
          </Overlay>
        );

      default:
        return '';
    }
  };

  return (
    <>
      {overlayVisibility && renderOverlay(overlayType)}

      {Object.keys(status).length && status.success ? (
        <Redirect to="/auth/clients" />
      ) : (
        ''
      )}

      {toastVisibility &&
        renderToast(
          toastInfos.toastType,
          toastInfos.toastMessage,
          toastInfos.toastCloseTime,
        )}

      <S.Container>
        <MDBRow>
          <MDBCol lg="12">
            <ReturnPreviousPage
              text="Voltar para gerência de projetos"
              handleClick={() => {
                const haveAlterations = () => {
                  setOverlayVisibility(true);
                  setOverlayType('returning');
                };

                const dontHaveAlterations = () => {
                  setToastVisibility(false);
                  history.push('/auth/clients');
                };

                return formAlterations
                  ? haveAlterations()
                  : dontHaveAlterations();
              }}
            />
            <S.Infos>
              {state ? (
                <>
                  <S.FeaturedText>Edição de cliente</S.FeaturedText>
                  <FeaturedText text={values.clientName || state.nome} />
                </>
              ) : (
                <>
                  <S.FeaturedText>Cadastro de clientes</S.FeaturedText>
                  <FeaturedText text="Cadastre novos clientes para a corporação" />
                </>
              )}
            </S.Infos>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol lg="6" sm="12">
            <S.Divider>Insira as informações do cliente</S.Divider>
            <S.Form>
              <InputField
                labelText="CNPJ do cliente (*)"
                placeholderText="Insira o CNPJ do cliente"
                width={50}
                name="clientNrle"
                value={values.clientNrle}
                handleChange={e => {
                  setFieldValue('clientNrle', nrleNumber(e.target.value));
                  setFormAlterations(true);
                }}
              />
              {!!errors.clientNrle && (
                <ErrorMessage message={errors.clientNrle} />
              )}

              <InputField
                labelText="Nome do cliente (*)"
                placeholderText="Qual o nome do cliente?"
                name="clientName"
                value={values.clientName}
                handleChange={e => {
                  setFieldValue('clientName', e.target.value);
                  setFormAlterations(true);
                }}
              />
              {!!errors.clientName && (
                <ErrorMessage message={errors.clientName} />
              )}

              <SelectField
                hasLabelMargin
                labelText="Gerente de contas (*)"
                placeholderText="Selecione um gerente de contas..."
                name="accountManager"
                options={sortArrayInAlphabeticalOrder(
                  accountManagersOptions,
                  'label',
                )}
                value={accountManagersOptions.find(
                  option => option.value === values.accountManager,
                )}
                handleChange={({ value }) => {
                  setFieldValue('accountManager', value);
                  setFormAlterations(true);
                }}
                isDisabled={isLoading}
              />
              {!!errors.accountManager && (
                <ErrorMessage message={errors.accountManager} />
              )}

              <SelectField
                hasLabelMargin
                labelText="Status do cliente (*)"
                placeholderText="Selecione o status..."
                name="clientStatus"
                options={statusOptions}
                value={statusOptions.find(
                  option => option.value === values.clientStatus,
                )}
                handleChange={({ value }) => {
                  setFieldValue('clientStatus', value);
                  setFormAlterations(true);
                }}
              />
              {!!errors.clientStatus && (
                <ErrorMessage message={errors.clientStatus} />
              )}

              <S.Buttons>
                <FilledButton
                  text="Cancelar"
                  buttonClass="cancel"
                  handleClick={() => {
                    setOverlayVisibility(true);
                    setOverlayType('cancel');
                  }}
                />
                <FilledButton
                  text="Cadastrar cliente"
                  buttonClass="confirm"
                  handleClick={() => {
                    setOverlayVisibility(true);
                    setOverlayType('addClient');
                    setOverlayAction(() => handleSubmit);
                  }}
                  disabled={!formAlterations}
                />
              </S.Buttons>
            </S.Form>
          </MDBCol>
        </MDBRow>
      </S.Container>
      <Footer />
    </>
  );
}

AddClients.defaultProps = {
  location: {},
  status: {},
  user: {},
};

AddClients.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
    state: PropTypes.objectOf(
      PropTypes.shape({
        id_cliente: PropTypes.number,
        nome: PropTypes.string,
        id_status_cliente: PropTypes.number,
        tipo_status: PropTypes.string,
        cnpj: PropTypes.string,
        id_func_account_manager: PropTypes.number || null,
        func_account_manager: PropTypes.string || null,
      }),
    ),
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  status: PropTypes.objectOf(PropTypes.string),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};

export default withFormik({
  validateOnBlur: false,
  validateOnChange: false,

  validationSchema,

  enableReinitialize: true,

  handleSubmit: (values, { setStatus }) => {
    const { history } = values;
    const data = {
      nome: values.clientName,
      id_usr_up: values.idFuncionario,
      id_status_cliente: values.clientStatus,
      id_func_account_manager: values.accountManager,
      cnpj: values.clientNrle,
    };
    if (values.clientId) {
      const putData = {
        id_cliente: values.clientId,
        ...data,
      };
      return post(insertClient, putData)
        .then(() =>
          localStorage.setItem(
            'successToast',
            JSON.stringify({
              toastType: 'success',
              toastMessage: 'Cliente atualizado com sucesso',
              toastCloseTime: 8000,
            }),
          ),
        )
        .then(() => setStatus({ success: true }))
        .then(() => history.push('/auth/clients'))
        .catch(err => {
          const { response } = err;
          const { status } = response;
          const message =
            status === 500
              ? 'Um cliente com o mesmo CNPJ está cadastrado  em nossa base de dados!'
              : 'Erro ao cadastrar cliente. Por favor tente novamente';
          setStatus({
            toastVisiblity: true,
            toastType: 'error',
            toastMessage: message,
            toastCloseTime: 8000,
          });
        });
    }

    return post(insertClient, {
      id_usr_in: values.idFuncionario,
      ...data,
    })
      .then(() =>
        localStorage.setItem(
          'successToast',
          JSON.stringify({
            toastType: 'success',
            toastMessage: 'Cliente criado com sucesso',
            toastCloseTime: 8000,
          }),
        ),
      )
      .then(() => setStatus({ success: true }))
      .catch(err => {
        const { response } = err;
        const { status } = response;
        const message =
          status === 500
            ? 'Um cliente com o mesmo CNPJ está cadastrado  em nossa base de dados!'
            : 'Erro ao cadastrar cliente. Por favor tente novamente';
        setStatus({
          toastVisiblity: true,
          toastType: 'error',
          toastMessage: message,
          toastCloseTime: 8000,
        });
      });
  },
})(AddClients);
